/**
 * Asset libraries (Princewill, 2026-11-04): "I can't add multiple
 * logo only replace which is not good... we need it for multiple
 * uploads for logos."
 *
 * Uploads are KEPT, not replaced. Each asset kind (bank logos,
 * signature images, pictures/stamps) has a small library persisted
 * in this browser under its own storage key. Uploading ADDS an
 * entry; tapping an entry places it; each entry can be deleted on
 * its own. "Replace" is never the silent default.
 *
 * The libraries are the user's asset collection, not part of any
 * one check design, so they deliberately live OUTSIDE the design
 * snapshot/history: undoing a design change must never un-upload a
 * logo, and every design the user opens can draw on the same
 * library. The design snapshot records only WHICH entry is placed
 * (activeLogoId / activeSignatureId) plus the placed image itself,
 * exactly as it did before libraries existed.
 *
 * Storage honesty: browser storage is finite, so each library has
 * a hard count cap. The cap is surfaced in the UI copy and an
 * upload that hits it is refused with a visible note - an upload
 * is NEVER silently dropped, and an existing entry is never
 * evicted to make room.
 */

export interface AssetEntry {
    id: string;
    /** Display name - the uploaded file's name. */
    name: string;
    /** The raw upload, as a data URL. Placement processing (logo
     * background/write-ups/crop, signature auto-clean) derives the
     * placed image from this, so options can re-derive without
     * re-uploading - same model the single-logo flow already used. */
    src: string;
    addedAt: number;
}

export type AssetKind = 'logos' | 'signatures' | 'pictures';

export const ASSET_LIBRARY_CAPS: Record<AssetKind, number> = {
    logos: 12,
    signatures: 12,
    pictures: 20
};

export const assetLibraryStorageKey = (kind: AssetKind): string => `oc-asset-library:v1:${kind}`;

let entryCounter = 0;

export function makeAssetEntry(name: string, src: string): AssetEntry {
    entryCounter += 1;
    return {
        id: `asset-${Date.now().toString(36)}-${entryCounter.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
        name: name || 'Upload',
        src,
        addedAt: Date.now()
    };
}

const isAssetEntry = (v: any): v is AssetEntry =>
    !!v && typeof v === 'object' && typeof v.id === 'string' && typeof v.src === 'string' && v.src.length > 0;

/** Load a library from browser storage. Anything missing, corrupt
 * or malformed reads as an empty library - a bad stored value can
 * never break the studio. */
export function loadAssetLibrary(kind: AssetKind): AssetEntry[] {
    try {
        const raw = localStorage.getItem(assetLibraryStorageKey(kind));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isAssetEntry).map(e => ({
            id: e.id,
            name: typeof e.name === 'string' && e.name ? e.name : 'Upload',
            src: e.src,
            addedAt: typeof e.addedAt === 'number' ? e.addedAt : 0
        }));
    } catch {
        return [];
    }
}

/** Persist a library. Returns false when storage refused (quota) -
 * the caller keeps the in-memory library for the session and the
 * UI note can say so; nothing is silently lost mid-session. */
export function saveAssetLibrary(kind: AssetKind, entries: AssetEntry[]): boolean {
    try {
        localStorage.setItem(assetLibraryStorageKey(kind), JSON.stringify(entries));
        return true;
    } catch {
        return false;
    }
}

export type AddAssetResult =
    | { added: true; entries: AssetEntry[]; entry: AssetEntry }
    | { added: false; entries: AssetEntry[]; reason: 'cap' }
    | { added: false; entries: AssetEntry[]; reason: 'duplicate'; existing: AssetEntry };

/**
 * Add an upload to a library. Pure - returns the next list.
 * - the same image (identical src) already kept is a 'duplicate':
 *   the caller activates the existing entry instead of storing a
 *   second copy;
 * - a full library is a 'cap' refusal: the existing entries are
 *   returned untouched (never evict to make room).
 */
export function addAssetEntry(entries: AssetEntry[], entry: AssetEntry, cap: number): AddAssetResult {
    const existing = entries.find(e => e.src === entry.src);
    if (existing) return { added: false, entries, reason: 'duplicate', existing };
    if (entries.length >= cap) return { added: false, entries, reason: 'cap' };
    return { added: true, entries: [...entries, entry], entry };
}

/** Remove one entry by id. Pure. */
export function removeAssetEntry(entries: AssetEntry[], id: string): AssetEntry[] {
    return entries.filter(e => e.id !== id);
}

/**
 * Adopt a pre-library single asset (a design snapshot saved before
 * libraries existed carries its logo/signature directly) into the
 * library, so the upgrade keeps it instead of orphaning it.
 * Returns the (possibly unchanged) list and the adopted entry's id
 * - the pre-existing entry's id when the image is already kept, or
 * null when there was nothing to adopt / no room to adopt it.
 */
export function migrateLegacyAsset(
    entries: AssetEntry[],
    legacy: { name: string; src: string } | null,
    cap: number
): { entries: AssetEntry[]; id: string | null } {
    if (!legacy || !legacy.src) return { entries, id: null };
    const existing = entries.find(e => e.src === legacy.src);
    if (existing) return { entries, id: existing.id };
    if (entries.length >= cap) return { entries, id: null };
    const entry = makeAssetEntry(legacy.name || 'Upload', legacy.src);
    return { entries: [...entries, entry], id: entry.id };
}
